// ===== Production WebRTC Manager =====

import { CALL_STATES, WEBRTC_EVENTS, ICE_CONNECTION_STATES } from '../constants/ChatConstants';

export class WebRTCManager {
  constructor(options = {}) {
    this.logger = options.logger;
    this.config = options.config || {};
    this.onCallStateChange = options.onCallStateChange || (() => {});
    this.onError = options.onError || (() => {});
    this.onMediaReady = options.onMediaReady || (() => {});
    this.onIncomingCall = options.onIncomingCall || (() => {});
    this.onIceCandidate = options.onIceCandidate || (() => {});
    this.onCallAnswer = options.onCallAnswer || (() => {});
    
    // WebRTC components
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.dataChannel = null;
    this.screenShareStream = null;
    
    // State management
    this.currentSessionId = null;
    this.callStartTime = null;
    this.iceCandidatesQueue = [];
    
    this.init();
  }

  init() {
    // Initialize WebRTC configuration
    this.webRTCConfig = {
      iceServers: this.config.iceServers || this.config.ICE_SERVERS || [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ],
      iceCandidatePoolSize: this.config.iceCandidatePoolSize || this.config.ICE_CANDIDATE_POOL_SIZE || 10,
      iceTransportPolicy: this.config.iceTransportPolicy || this.config.ICE_TRANSPORT_POLICY || 'all',
      bundlePolicy: this.config.bundlePolicy || this.config.BUNDLE_POLICY || 'max-bundle',
      rtcpMuxPolicy: this.config.rtcpMuxPolicy || this.config.RTCP_MUX_POLICY || 'require'
    };
    
    // Debug log to check the ICE servers
    this.logger?.info('WebRTC configuration initialized:', {
      iceServersCount: this.webRTCConfig.iceServers.length,
      iceServers: this.webRTCConfig.iceServers.map(server => ({
        urls: server.urls,
        hasCredentials: !!(server.username && server.credential)
      }))
    });
  }

  async startCall(isVideo, receiverId) {
    try {
      this.logger?.info('Starting WebRTC call', { isVideo, receiverId });
      
      const sessionId = `webrtc-${Date.now()}-${Math.random()}`;
      this.currentSessionId = sessionId;
      this.callStartTime = Date.now();
      
      // Get media stream
      const stream = await this.getUserMedia(isVideo);
      this.localStream = stream;
      
      // Create peer connection (caller creates the data channel)
      this.createPeerConnection(true);

      // Add tracks
      stream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, stream);
      });

      // Create offer
      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: isVideo
      });
      
      await this.peerConnection.setLocalDescription(offer);
      
      // Notify media ready
      this.onMediaReady(this.localStream, this.remoteStream);
      
      // Return both sessionId and offer for socket transmission
      return {
        sessionId,
        offer
      };
      
    } catch (error) {
      this.logger?.error('Failed to start call', error);
      this.onError(error);
      throw error;
    }
  }

  async acceptCall(callData) {
    try {
      this.logger?.info('Accepting WebRTC call', callData);
      
      this.currentSessionId = callData.sessionId;
      this.callStartTime = Date.now();
      
      // Get media stream
      const stream = await this.getUserMedia(callData.callType === 'video');
      this.localStream = stream;
      
      // Create peer connection (callee listens for remote data channel)
      this.createPeerConnection(false);

      // Add tracks
      stream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, stream);
      });

      // Set remote description
      await this.peerConnection.setRemoteDescription(callData.offer);
      
      // Process queued ICE candidates
      while (this.iceCandidatesQueue.length > 0) {
        const candidate = this.iceCandidatesQueue.shift();
        await this.peerConnection.addIceCandidate(candidate);
      }
      
      // Create answer
      const answer = await this.peerConnection.createAnswer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: callData.callType === 'video'
      });
      
      await this.peerConnection.setLocalDescription(answer);
      
      // Notify media ready
      this.onMediaReady(this.localStream, this.remoteStream);
      
      // Return the answer to be sent via socket
      return {
        answer,
        sessionId: this.currentSessionId
      };
      
    } catch (error) {
      this.logger?.error('Failed to accept call', error);
      this.onError(error);
      throw error;
    }
  }

  createPeerConnection(isInitiator = false) {
    try {
      this.peerConnection = new RTCPeerConnection(this.webRTCConfig);

      // Handle ICE candidates
      this.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.logger?.debug('ICE candidate generated', event.candidate.type);
          // Send ICE candidate via socket to remote peer
          if (this.onIceCandidate) {
            this.onIceCandidate({
              candidate: event.candidate,
              sessionId: this.currentSessionId
            });
          }
        }
      };

      // Handle remote stream
      this.peerConnection.ontrack = (event) => {
        this.logger?.info('Remote track received', event.track.kind);
        if (event.streams && event.streams.length > 0) {
          this.remoteStream = event.streams[0];
          this.onMediaReady(this.localStream, this.remoteStream);
        }
      };

      // Handle connection state changes
      this.peerConnection.onconnectionstatechange = () => {
        const state = this.peerConnection.connectionState;
        this.logger?.info('Connection state changed', state);

        switch (state) {
          case 'connecting':
            this.onCallStateChange(CALL_STATES.CONNECTING);
            break;
          case 'connected':
            this.onCallStateChange(CALL_STATES.CONNECTED);
            break;
          case 'disconnected':
            this.onCallStateChange(CALL_STATES.RECONNECTING);
            break;
          case 'failed':
            this.onCallStateChange(CALL_STATES.FAILED);
            break;
          case 'closed':
            this.onCallStateChange(CALL_STATES.ENDED);
            break;
        }
      };

      // Handle ICE connection state changes
      this.peerConnection.oniceconnectionstatechange = () => {
        const state = this.peerConnection.iceConnectionState;
        this.logger?.info('ICE connection state changed', state);

        if (state === 'failed') {
          // Attempt ICE restart
          this.peerConnection.restartIce();
        }
      };

      if (isInitiator) {
        // Caller creates the data channel
        this.dataChannel = this.peerConnection.createDataChannel('messages', { ordered: true });
        this.setupDataChannel();
      } else {
        // Callee waits for the remote data channel
        this.peerConnection.ondatachannel = (event) => {
          this.logger?.info('Data channel received from remote peer');
          this.dataChannel = event.channel;
          this.setupDataChannel();
        };
      }

    } catch (error) {
      this.logger?.error('Failed to create peer connection', error);
      this.onError(error);
      throw error;
    }
  }

  setupDataChannel() {
    if (!this.dataChannel) return;
    this.dataChannel.onopen = () => {
      this.logger?.info('Data channel opened');
    };
    this.dataChannel.onmessage = (event) => {
      this.logger?.debug('Data channel message received', event.data);
    };
  }

  async getUserMedia(isVideo) {
    try {
      const constraints = {
        video: isVideo ? this.config.VIDEO_CONSTRAINTS || {
          width: { ideal: 640 },
          height: { ideal: 480 }
        } : false,
        audio: this.config.AUDIO_CONSTRAINTS || {
          echoCancellation: true,
          noiseSuppression: true
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.logger?.info('Media stream obtained', {
        video: isVideo,
        tracks: stream.getTracks().length
      });
      
      return stream;
      
    } catch (error) {
      this.logger?.error('Failed to get user media', error);
      this.onError(error);
      throw error;
    }
  }

  toggleMute() {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        this.logger?.info('Audio toggled', { muted: !audioTrack.enabled });
        return !audioTrack.enabled;
      }
    }
    return false;
  }

  toggleVideo() {
    if (this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        this.logger?.info('Video toggled', { off: !videoTrack.enabled });
        return !videoTrack.enabled;
      }
    }
    return false;
  }

  toggleSpeaker() {
    // Speaker toggle logic would depend on the specific implementation
    this.logger?.info('Speaker toggled');
    return true; // Return current speaker state
  }

  async startScreenShare() {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });
      
      this.screenShareStream = screenStream;
      
      // Replace video track
      if (this.peerConnection && this.localStream) {
        const videoTrack = screenStream.getVideoTracks()[0];
        const sender = this.peerConnection.getSenders().find(s => 
          s.track && s.track.kind === 'video'
        );
        
        if (sender && videoTrack) {
          await sender.replaceTrack(videoTrack);
        }
      }
      
      this.logger?.info('Screen share started');
      
    } catch (error) {
      this.logger?.error('Failed to start screen share', error);
      this.onError(error);
      throw error;
    }
  }

  async stopScreenShare() {
    try {
      if (this.screenShareStream) {
        this.screenShareStream.getTracks().forEach(track => track.stop());
        this.screenShareStream = null;
      }
      
      // Restore camera
      if (this.peerConnection && this.localStream) {
        const cameraStream = await this.getUserMedia(true);
        const videoTrack = cameraStream.getVideoTracks()[0];
        const sender = this.peerConnection.getSenders().find(s => 
          s.track && s.track.kind === 'video'
        );
        
        if (sender && videoTrack) {
          await sender.replaceTrack(videoTrack);
        }
      }
      
      this.logger?.info('Screen share stopped');
      
    } catch (error) {
      this.logger?.error('Failed to stop screen share', error);
      this.onError(error);
      throw error;
    }
  }

  // Handle incoming call answer
  async handleCallAnswer(answerData) {
    try {
      this.logger?.info('Handling call answer', answerData);
      
      if (!this.peerConnection) {
        throw new Error('No peer connection available');
      }
      
      // Check if we can set remote description
      const signalingState = this.peerConnection.signalingState;
      this.logger?.debug('Peer connection signaling state:', signalingState);
      
      if (signalingState === 'have-local-offer') {
        await this.peerConnection.setRemoteDescription(answerData.answer);
        this.logger?.info('Remote answer set successfully');
      } else {
        this.logger?.warn('Cannot set remote answer, peer connection state:', signalingState);
        // Connection might already be established, which is fine
        if (signalingState === 'stable') {
          this.logger?.info('Peer connection already in stable state, call likely established');
        }
      }
      
      // Process queued ICE candidates
      while (this.iceCandidatesQueue.length > 0) {
        const candidate = this.iceCandidatesQueue.shift();
        try {
          await this.peerConnection.addIceCandidate(candidate);
        } catch (iceError) {
          this.logger?.debug('Failed to add queued ICE candidate (not critical):', iceError);
        }
      }
      
      this.logger?.info('Call answer handled successfully');
      
    } catch (error) {
      this.logger?.error('Failed to handle call answer', error);
      // Don't propagate this error as it might be a timing issue and call could still work
      // this.onError(error);
    }
  }

  // Handle incoming ICE candidate
  async handleIceCandidate(candidateData) {
    try {
      this.logger?.debug('Handling ICE candidate', candidateData);
      
      if (!this.peerConnection) {
        this.logger?.warn('No peer connection, queuing ICE candidate');
        this.iceCandidatesQueue.push(candidateData.candidate);
        return;
      }
      
      if (this.peerConnection.remoteDescription) {
        await this.peerConnection.addIceCandidate(candidateData.candidate);
        this.logger?.debug('ICE candidate added successfully');
      } else {
        this.logger?.debug('Remote description not set, queuing ICE candidate');
        this.iceCandidatesQueue.push(candidateData.candidate);
      }
      
    } catch (error) {
      this.logger?.error('Failed to handle ICE candidate', error);
      // Don't throw error for ICE candidate failures as they're not critical
    }
  }

  endCall() {
    try {
      this.logger?.info('Ending WebRTC call - starting comprehensive cleanup');
      
      // Stop all local stream tracks (camera, microphone) with enhanced cleanup
      if (this.localStream) {
        this.logger?.info('Stopping local stream tracks');
        this.localStream.getTracks().forEach(track => {
          this.logger?.debug(`Stopping ${track.kind} track: ${track.label}, ready state: ${track.readyState}`);
          if (track.readyState === 'live') {
            track.stop();
            this.logger?.debug(`Track ${track.kind} stopped successfully`);
          }
        });
        
        // Clear the stream reference
        this.localStream = null;
        this.logger?.info('Local stream reference cleared');
      }
      
      // Stop screen share stream with enhanced cleanup
      if (this.screenShareStream) {
        this.logger?.info('Stopping screen share stream');
        this.screenShareStream.getTracks().forEach(track => {
          this.logger?.debug(`Stopping screen share ${track.kind} track: ${track.label}, ready state: ${track.readyState}`);
          if (track.readyState === 'live') {
            track.stop();
            this.logger?.debug(`Screen share track ${track.kind} stopped successfully`);
          }
        });
        
        // Clear the stream reference
        this.screenShareStream = null;
        this.logger?.info('Screen share stream reference cleared');
      }
      
      // Stop remote stream tracks if available
      if (this.remoteStream) {
        this.logger?.info('Stopping remote stream tracks');
        this.remoteStream.getTracks().forEach(track => {
          this.logger?.debug(`Stopping remote ${track.kind} track: ${track.label}, ready state: ${track.readyState}`);
          if (track.readyState === 'live') {
            track.stop();
            this.logger?.debug(`Remote track ${track.kind} stopped successfully`);
          }
        });
        
        // Clear the stream reference
        this.remoteStream = null;
        this.logger?.info('Remote stream reference cleared');
      }
      
      // Remove tracks from peer connection senders before closing
      if (this.peerConnection) {
        this.logger?.info('Removing tracks from peer connection');
        const senders = this.peerConnection.getSenders();
        senders.forEach(sender => {
          if (sender.track) {
            this.logger?.debug(`Removing ${sender.track.kind} track from peer connection`);
            sender.track.stop();
            this.peerConnection.removeTrack(sender);
          }
        });
      }
      
      // Close data channel
      if (this.dataChannel) {
        this.logger?.info('Closing data channel');
        this.dataChannel.close();
        this.dataChannel = null;
      }
      
      // Close peer connection with proper cleanup
      if (this.peerConnection) {
        this.logger?.info('Closing peer connection');
        
        // Remove all event listeners
        this.peerConnection.onicecandidate = null;
        this.peerConnection.ontrack = null;
        this.peerConnection.onconnectionstatechange = null;
        this.peerConnection.oniceconnectionstatechange = null;
        this.peerConnection.ondatachannel = null;
        this.peerConnection.onsignalingstatechange = null;
        this.peerConnection.onremovetrack = null;
        
        // Close the connection
        this.peerConnection.close();
        this.peerConnection = null;
        this.logger?.info('Peer connection closed and cleared');
      }
      
      // Force garbage collection of media resources
      if (typeof window !== 'undefined' && window.gc) {
        try {
          window.gc();
          this.logger?.debug('Forced garbage collection');
        } catch (e) {
          // Ignore garbage collection errors
        }
      }
      
      // Reset state
      this.currentSessionId = null;
      this.callStartTime = null;
      this.iceCandidatesQueue = [];
      
      this.logger?.info('WebRTC call cleanup completed successfully');
      this.onCallStateChange(CALL_STATES.ENDED);
      
    } catch (error) {
      this.logger?.error('Error during call cleanup', error);
      this.onError(error);
      
      // Force cleanup even if errors occurred
      this.localStream = null;
      this.screenShareStream = null;
      this.remoteStream = null;
      this.peerConnection = null;
      this.dataChannel = null;
    }
  }

  cleanup() {
    this.endCall();
  }
}

export default WebRTCManager;
